"use client";

import React, { useState, useEffect } from "react";
import Image from "next/image";
import Link from "next/link";

interface SoftwareSlide {
  href: string;
  image: string;
  alt: string;
}

interface SoftwareSlideCarouselProps {
  slides: SoftwareSlide[];
}

export default function SoftwareSlideCarousel({ slides }: SoftwareSlideCarouselProps) {
  const [currentSlide, setCurrentSlide] = useState(0);

  // Reset currentSlide when slides change
  useEffect(() => {
    setCurrentSlide(0);
  }, [slides.length]);

  // Clamp currentSlide when slides are removed
  useEffect(() => {
    if (slides.length > 0 && currentSlide >= slides.length) {
      setCurrentSlide(0);
    }
  }, [slides.length, currentSlide]);

  // Auto-play effect with proper guards
  useEffect(() => {
    if (slides.length <= 1) {
      // No rotation needed for 0 or 1 slide
      setCurrentSlide(0);
      return;
    }

    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 3000);

    return () => clearInterval(interval);
  }, [slides.length]);

  // Early return if no slides to avoid rendering and intervals
  if (slides.length === 0) {
    return null;
  }

  // Safe current slide value
  const safeCurrentSlide = Math.max(0, Math.min(currentSlide, slides.length - 1));

  return (
    <div className="w-2/3 mx-auto">
      <div className="relative overflow-hidden rounded-lg">
        <div 
          className="flex transition-transform duration-500 ease-in-out"
          style={{ 
            width: `${slides.length * 100}%`,
            transform: `translateX(-${safeCurrentSlide * (100 / slides.length)}%)` 
          }}
        >
          {slides.map((slide, index) => (
            <div 
              key={index} 
              className="flex-shrink-0"
              style={{ width: `${100 / slides.length}%` }}
            >
              <Link href={slide.href} className="block">
                <Image
                  src={slide.image}
                  alt={slide.alt}
                  title={slide.alt}
                  width={425}
                  height={425}
                  className="w-full h-auto "
                />
              </Link>
            </div>
          ))}
        </div>
      </div>
      
      {/* Pagination Dots - only show if more than 1 slide */}
      {slides.length > 1 && (
        <div className="flex justify-center mt-4 space-x-2">
          {slides.map((_, index) => (
            <button
              key={index}
              type="button"
              onClick={() => setCurrentSlide(index)}
              className={`w-3 h-3 rounded-full transition-colors ${
                index === safeCurrentSlide ? 'bg-accent' : 'bg-gray-300'
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}

